<template>
  <b-modal id="add_contact_modal" ref="add_contact_modal" hide-footer no-close-on-esc no-close-on-backdrop>
    <div slot="modal-header" class="mh">
      <div id="mh-text">
        <!-- Different modal header depending on type note being left -->
        <h1>
          <i class="fas fa-user-plus"></i>
          <span>Add Contact</span>
        </h1>
        <p id="mh-info">Add a new contact at {{ $store.state.contacts.modal.company_name }}</p>
      </div>
      <button id="mh-close" class="btn-close" @click="hideModal()"><i class="fas fa-times"></i></button>
    </div>
    <div>
      <ValidationObserver ref="addContactForm" slim>
        <form role="form" @submit.prevent="create_contact()">
          <div class="row">
            <!-- Job Title -->
            <div class="col-12 form-group">
              <validationProvider rules="required" v-slot="{errors, classes}" name="Job Title" tag="div">
                <label class="form-label" for="job_title">Job Title</label>
                <multiselect v-model="contact.job_title" placeholder="" label="job_title" id="job_title" track-by="id" :options="ms_opts.job_titles" :option-height="200"
                             :custom-label="job_title_label" :show-labels="false" class="requiredField" :class="classes">
                  <template slot="singleLabel" slot-scope="props">
                    <span class="option__desc_single">
                      <span class="option__title">{{ props.option.job_title }}</span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <div class="option__desc">
                      <span class="option__title">{{ props.option.job_title }}</span>
                      <span class="option__small">{{ props.option.sector_name }}</span>
                    </div>
                  </template>
                </multiselect>
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </validationProvider>
            </div>
            <!-- Title -->
            <div class="col-12 form-group">
              <validationProvider rules="required" v-slot="{errors, classes}" name="Title" tag="div">
                <label class="form-label" for="title">Title</label>
                <multiselect v-model="contact.title" id="title" name="title" placeholder="" class="requiredField" :class="classes" label="value"
                             :options="ms_opts.titles"></multiselect>
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </validationProvider>
            </div>
            <!-- First Name -->
            <div class="col-xl-6 form-group">
              <validationProvider rules="required" v-slot="{errors, classes}" name="First Name" tag="div">
                <label class="form-label" for="first_name">First Name</label>
                <input v-model="contact.first_name" name="first_name" type="text" class="form-input requiredField" :class="classes" id="first_name"
                       @input="forceCapitalize($event, contact, 'first_name')">
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </validationProvider>
            </div>
            <!-- Surname -->
            <div class="col-xl-6 form-group">
              <validationProvider rules="required" v-slot="{errors, classes}" name="Surname" tag="div">
                <label class="form-label" for="surname">Surname</label>
                <input v-model="contact.surname" name="surname" type="text" class="form-input requiredField" :class="classes" id="surname"
                       @input="forceCapitalize($event, contact, 'surname')">
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </validationProvider>
            </div>
            <!-- Phone Number -->
            <div class="col-xl-6 form-group">
              <ValidationProvider rules="decimal|max:15" v-slot="{errors, classes}" name="Phone Number" tag="div">
                <label class="form-label" for="phone">Phone Number</label>
                <input type="text" v-model="contact.phone" id="phone" name="phone" class="form-input" :class="classes" maxlength="15">
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>
            <!-- Mobile Number -->
            <div class="col-xl-6 form-group">
              <ValidationProvider rules="decimal|max:15" v-slot="{errors, classes}" name="Mobile Number" tag="div">
                <label class="form-label" for="mobile">Mobile Number</label>
                <input type="text" v-model="contact.mobile" id="mobile" name="mobile" class="form-input" :class="classes" maxlength="15">
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>
            <!-- Email -->
            <div class="col-12 form-group">
              <ValidationProvider rules="email" v-slot="{errors, classes}" name="Email Address" tag="div">
                <label class="form-label" for="email">Email Address</label>
                <input type="email" v-model="contact.email" id="email" class="form-input" :class="classes">
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>
            <!-- Contact Address -->
            <div class="col-12 mt-2">
              <div class="row">
                <label class="col-xl-8 cardLabel" for="is_company_address">Contact at company address</label>
                <div class="col-md-4 form-group">
                  <bootstrap-toggle v-model="contact.address_same_as_company" id="is_company_address" name="is_company_address" :options="{ on: 'Yes', off: 'No' }"/>
                </div>
              </div>
            </div>

            <div class="col-12 form-group" v-if="contact.address_same_as_company">
              <ValidationProvider rules="required" v-slot="{errors, classes}" name="Company Address" tag="div">
                <label class="form-label" for="company_address">Company Address</label>
                <multiselect v-model="company_address" id="company_address" name="company_address" placeholder="" class="requiredField" :class="classes"
                             :options="ms_opts.company_addresses" :custom-label="address_label"></multiselect>
                <div v-if="errors[0]" class="vvERROR">{{ errors[0] }}</div>
              </ValidationProvider>
            </div>

            <div class="col-12" v-if="!contact.address_same_as_company">
              <div class="row">
                <div class="col-xl-12 form-group">
                  <label class="form-label" for="contact_address">Address (Google Maps Search)</label>
                  <GmapAutocomplete id="contact_address" name="contact_address" class="form-input input" placeholder="" @place_changed="set_address"
                                    :value="full_address" @input.native="watch_address" @keypress.enter="$event.preventDefault()"></GmapAutocomplete>
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_building">Building</label>
                  <input type="text" v-model="address.building" id="personal_building" name="personal_building" class="form-input">
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_street">Street</label>
                  <input type="text" v-model="address.street" id="personal_street" name="personal_street" class="form-input">
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_locality">Locality</label>
                  <input type="text" v-model="address.locality" id="personal_locality" name="personal_locality" class="form-input">
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_city_town">City / Town</label>
                  <input type="text" v-model="address.city_town" id="personal_city_town" name="personal_city_town" class="form-input">
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_county">County</label>
                  <input type="text" v-model="address.county" id="personal_county" name="personal_county" class="form-input">
                </div>
                <div class="col-6 form-group">
                  <label class="form-label" for="personal_postcode">Postcode</label>
                  <input type="text" v-model="address.postcode" id="personal_postcode" name="personal_postcode" class="form-input">
                </div>
              </div>
            </div>

            <div class="col-12">
              <div class="float-right">
                <button type="submit" class="btn orcaBtn">SAVE and CLOSE</button>
              </div>
            </div>
          </div>
        </form>
      </ValidationObserver>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect';
import BootstrapToggle from 'vue-bootstrap-toggle';
import Vue from "vue";
import * as GmapVue from 'gmap-vue'

Vue.use(GmapVue, {
  load: {
    key: process.env.MIX_API_GMAPS_KEY, // LIVE
    libraries: 'places',
    region: 'GB'
  }
});

export default {
  name: "add_contact",
  components: {
    Multiselect,
    BootstrapToggle
  },
  data() {
    return {
      ms_opts: {
        job_titles: this.$store.state.crm_options.job_titles,
        titles: this.$store.state.crm_options.person_title,
        company_addresses: []
      },

      contact: {
        company_id: this.$store.state.contacts.modal.company_id,
        job_title: '',
        title: null,
        first_name: '',
        surname: '',
        phone: '',
        mobile: '',
        email: '',
        address_same_as_company: true,
        address: '',
        log_by_type: 'App\\Models\\Users\\User',
        log_by_id: this.$store.state.auth.me.id
      },

      company_address: null,

      full_address: null,
      address: {
        building: null,
        street: null,
        locality: null,
        city_town: null,
        county: null,
        postcode: null,
      },
    }
  },
  beforeMount() {
    axios.get(this.crmApiPath + 'contact_info/address/company/' + this.$store.state.contacts.modal.company_id).then(company_addresses => {
      this.ms_opts.company_addresses = company_addresses.data;
    })
  },
  methods: {
    job_title_label({sector_name, job_title}) {
      return `${job_title} - ${sector_name}`
    },
    address_label({full, address_type}) {
      return `[${address_type}] ${full}`;
    },
    set_address(place) {
      let address = this.set_google_address(place);
      // set all address variables
      this.address.building = address.building;
      this.address.street = address.street;
      this.address.locality = address.locality;
      this.address.city_town = address.city_town;
      this.address.county = address.county;
      this.address.postcode = address.postcode;
    },
    watch_address(place) {
      if (place.path[0].value === '') {
        this.full_address = null;
        this.address.building = null;
        this.address.street = null;
        this.address.locality = null;
        this.address.city_town = null;
        this.address.county = null;
        this.address.postcode = null;
      }
    },
    hideModal() {
      this.$refs.add_contact_modal.hide();
      this.$store.commit('HIDE_CONTACT_MODAL');
    },
    create_contact() {
      this.$refs.addContactForm.validate().then(success => {
        if (!success) {
          this.formErrorsValidationScroll(this.$refs.addContactForm);
        }
        else {
          this.contact.title_id = this.contact.title.id
          axios.post(this.crmApiPath + 'contact', this.contact).then(contact_id => {
            this.address.company_id = this.$store.state.contacts.modal.company_id;
            this.address.belongs_to_id = contact_id.data;
            this.address.belongs_to_type = 'App\\Models\\Companies\\Contacts\\Contact';
            this.address.contact_info_model_type = 'App\\Models\\Contact_Info\\Address';
            this.address.log_by_id = this.$store.state.auth.me.id;
            this.address.record_type = 'contact';

            if (this.contact.address_same_as_company) {
              this.address.address_type_id = this.company_address.address_type_id;
              this.address.building = this.company_address.building;
              this.address.street = this.company_address.street;
              this.address.locality = this.company_address.locality;
              this.address.city_town = this.company_address.city_town;
              this.address.county = this.company_address.county;
              this.address.postcode = this.company_address.postcode;
              this.address.full = this.company_address.full;
            }
            else {
              this.address.address_type_id = 46;
              this.address.full = this.full_address;
            }

            axios.post(this.crmApiPath + 'contact_info/address', this.address).then(() => {
              let contact_promises = [];
              if (this.contact.mobile !== '') {
                contact_promises.push(new Promise(resolve => {
                  let mobile = {
                    'company_id': this.$store.state.contacts.modal.company_id,
                    'contact_id': contact_id.data,
                    'belongs_to_id': contact_id.data,
                    'belongs_to_type': 'App\\Models\\Companies\\Contacts\\Contact',
                    'contact_info_model_type': 'App\\Models\\Contact_Info\\Phone',
                    'log_by_id': this.$store.state.auth.me.id,
                    'record_type': 'contact',
                    'phone_type_id': 57, // work mobile
                    'phone_number': this.contact.mobile
                  }
                  axios.post(this.crmApiPath + 'contact_info/phone', mobile).then(() => resolve());
                }));
              }
              else {
                contact_promises.push(new Promise(resolve => resolve()));
              }
              if (this.contact.phone !== '') {
                contact_promises.push(new Promise(resolve => {
                  let phone = {
                    'company_id': this.$store.state.contacts.modal.company_id,
                    'contact_id': contact_id.data,
                    'belongs_to_id': contact_id.data,
                    'belongs_to_type': 'App\\Models\\Companies\\Contacts\\Contact',
                    'contact_info_model_type': 'App\\Models\\Contact_Info\\Phone',
                    'log_by_id': this.$store.state.auth.me.id,
                    'record_type': 'contact',
                    'phone_type_id': 56, // work landline
                    'phone_number': this.contact.phone
                  }
                  axios.post(this.crmApiPath + 'contact_info/phone', phone).then(() => resolve());
                }));
              }
              else {
                contact_promises.push(new Promise(resolve => resolve()));
              }
              if (this.contact.email !== '') {
                contact_promises.push(new Promise(resolve => {
                  let email = {
                    'company_id': this.$store.state.contacts.modal.company_id,
                    'contact_id': contact_id.data,
                    'belongs_to_id': contact_id.data,
                    'belongs_to_type': 'App\\Models\\Companies\\Contacts\\Contact',
                    'contact_info_model_type': 'App\\Models\\Contact_Info\\Email',
                    'log_by_id': this.$store.state.auth.me.id,
                    'record_type': 'contact',
                    'email_type_id': 63, // work email
                    'email_address': this.contact.email
                  }
                  axios.post(this.crmApiPath + 'contact_info/email', email).then(() => resolve());
                }));
              }
              else {
                contact_promises.push(new Promise(resolve => resolve()));
              }
              contact_promises.push(new Promise(resolve => {
                let contact_profile = {
                  'contact_id': contact_id.data,
                  'job_title_id': this.contact.job_title.id,
                  'company_id': this.$store.state.contacts.modal.company_id,
                  'log_by_type': 'App\\Models\\Users\\User',
                  'log_by_id': this.$store.state.auth.me.id
                }
                axios.post(this.crmApiPath + 'contact/profile', contact_profile).then(() => resolve());
              }));
              Promise.all(contact_promises).then(() => {
                this.$snotify.success('Contact added to ' + this.$store.state.contacts.modal.company_name, 'Contact Added');
                this.$bus.$emit('contacts::reload', {action: true});
                this.$bus.$emit('logs::reload', {type: 'contact', action: true});
                this.$bus.$emit('table::reload', {table: 'all_contacts', type: 'store'});
                this.hideModal();
              });
            });
          })
        }
      })
    }
  },
  watch: {
    'contact.address_same_as_company'(val) {
      if (!val) {
        this.company_address = null;
      }
      else if (val) {
        this.full_address = null;
        this.address.building = null;
        this.address.street = null;
        this.address.locality = null;
        this.address.city_town = null;
        this.address.county = null;
        this.address.postcode = null;
      }
    },
    'address.building'(building) {
      this.full_address = this.calc_nice_address(building, this.address.street, this.address.locality, this.address.city_town, this.address.county, this.address.postcode);
    },
    'address.street'(street) {
      this.full_address = this.calc_nice_address(this.address.building, street, this.address.locality, this.address.city_town, this.address.county, this.address.postcode);
    },
    'address.locality'(locality) {
      this.full_address = this.calc_nice_address(this.address.building, this.address.street, locality, this.address.city_town, this.address.county, this.address.postcode);
    },
    'address.city_town'(city_town) {
      this.full_address = this.calc_nice_address(this.address.building, this.address.street, this.address.locality, city_town, this.address.county, this.address.postcode);
    },
    'address.county'(county) {
      this.full_address = this.calc_nice_address(this.address.building, this.address.street, this.address.locality, this.address.city_town, county, this.address.postcode);
    },
    'address.postcode'(postcode) {
      this.full_address = this.calc_nice_address(this.address.building, this.address.street, this.address.locality, this.address.city_town, this.address.county, postcode);
    },
  }
}
</script>

<style lang="scss">
#add_contact_modal {
  .multiselect__option {
    height: auto !important;
  }

  .option__desc {
    .option__title {
      font-weight: bold;
      display:     block;
    }

    .option__small {
      font-size: 0.7rem;
      display:   block;
    }
  }
}
</style>
